import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Loader2, Save, Euro, AlertCircle, CheckCircle2, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// KM tiers (static)
const KM_TIERS = [
  { id: "100", label: "100 km" },
  { id: "200", label: "200 km" },
  { id: "300", label: "300 km" },
  { id: "400", label: "400 km" },
  { id: "500", label: "500 km" },
  { id: "illimitati", label: "Illimitati" },
];

export default function CompanyPricing() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricing, setPricing] = useState({});
  const [extraKmCosts, setExtraKmCosts] = useState({});
  const [vehicleTypes, setVehicleTypes] = useState([]);

  useEffect(() => {
    if (token && user?.company_id) {
      fetchData();
    }
  }, [token, user]);

  const fetchData = async () => {
    try {
      // Fetch vehicle types from API
      const typesRes = await axios.get(`${API}/vehicle-types`);
      const types = typesRes.data;
      setVehicleTypes(types);
      
      // Fetch pricing
      const pricingRes = await axios.get(`${API}/companies/${user.company_id}/pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert pricing array to object structure for easier editing
      const pricingObj = {};
      const extraKmObj = {};
      
      for (const vt of types) {
        pricingObj[vt.id] = {};
        extraKmObj[vt.id] = 0.30; // Default
        
        for (const tier of KM_TIERS) {
          pricingObj[vt.id][tier.id] = "";
        }
      }
      
      // Fill with existing data
      if (pricingRes.data.pricing) {
        for (const vtPricing of pricingRes.data.pricing) {
          extraKmObj[vtPricing.vehicle_type] = vtPricing.extra_km_cost || 0.30;
          
          for (const tier of vtPricing.tiers || []) {
            if (pricingObj[vtPricing.vehicle_type]) {
              pricingObj[vtPricing.vehicle_type][tier.km_tier] = tier.price || "";
            }
          }
        }
      }
      
      setPricing(pricingObj);
      setExtraKmCosts(extraKmObj);
    } catch (error) {
      toast.error("Errore nel caricamento tariffe");
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (vehicleType, kmTier, value) => {
    setPricing(prev => ({
      ...prev,
      [vehicleType]: {
        ...prev[vehicleType],
        [kmTier]: value
      }
    }));
  };

  const handleExtraKmChange = (vehicleType, value) => {
    setExtraKmCosts(prev => ({
      ...prev,
      [vehicleType]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Convert object structure back to array for API
      const pricingArray = [];
      const validationErrors = [];
      
      for (const vt of vehicleTypes) {
        const tiers = [];
        let hasAnyPrice = false;
        
        for (const tier of KM_TIERS) {
          const price = parseFloat(pricing[vt.id]?.[tier.id]) || 0;
          if (price > 0) {
            hasAnyPrice = true;
            tiers.push({
              km_tier: tier.id,
              price: price
            });
          }
        }
        
        if (hasAnyPrice) {
          // Validate extra_km_cost is set when there are prices
          const extraKm = parseFloat(extraKmCosts[vt.id]);
          if (!extraKm || extraKm <= 0) {
            validationErrors.push(`${vt.label}: inserisci il costo per km extra`);
            continue;
          }
          
          pricingArray.push({
            vehicle_type: vt.id,
            tiers: tiers,
            extra_km_cost: extraKm
          });
        }
      }
      
      if (validationErrors.length > 0) {
        toast.error(validationErrors.join("\n"));
        setSaving(false);
        return;
      }
      
      await axios.put(
        `${API}/companies/${user.company_id}/pricing`,
        { pricing: pricingArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Tariffe salvate con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Gestione <span className="text-primary">Tariffe</span>
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Imposta le tariffe giornaliere per tipologia di veicolo e scaglione km
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Salva Tariffe
              </>
            )}
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Come funziona il tariffario:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Inserisci il prezzo giornaliero per ogni combinazione tipologia/km</li>
              <li>I clienti potranno scegliere lo scaglione km durante la prenotazione</li>
              <li>Imposta il costo extra per km oltre la soglia scelta</li>
              <li>Lascia vuoti i campi per le combinazioni che non offri</li>
            </ul>
          </div>
        </div>

        {/* Pricing Table */}
        <div className="card-dashboard overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium text-sm">Tipologia</th>
                {KM_TIERS.map(tier => (
                  <th key={tier.id} className="text-center py-3 px-2 font-medium text-sm min-w-[100px]">
                    {tier.label}
                  </th>
                ))}
                <th className="text-center py-3 px-2 font-medium text-sm min-w-[120px]">
                  €/km extra
                </th>
              </tr>
            </thead>
            <tbody>
              {vehicleTypes.map(vt => (
                <tr key={vt.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="py-3 px-4">
                    <span className="font-medium">{vt.label}</span>
                    {vt.category && vt.category !== "furgone" && (
                      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        {vt.category}
                      </span>
                    )}
                  </td>
                  {KM_TIERS.map(tier => (
                    <td key={tier.id} className="py-2 px-2">
                      <div className="relative">
                        <Euro className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={pricing[vt.id]?.[tier.id] || ""}
                          onChange={(e) => handlePriceChange(vt.id, tier.id, e.target.value)}
                          className="pl-7 text-center h-9 text-sm"
                        />
                      </div>
                    </td>
                  ))}
                  <td className="py-2 px-2">
                    <div className="relative">
                      <Euro className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.30"
                        value={extraKmCosts[vt.id] || ""}
                        onChange={(e) => handleExtraKmChange(vt.id, e.target.value)}
                        className="pl-7 text-center h-9 text-sm"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 rounded"></div>
            <span>Prezzo impostato</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 rounded border border-dashed"></div>
            <span>Non disponibile (campo vuoto)</span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
